/**
 * The installation layer of the key `support` — remote support's standing
 * access — on master's PUBLIC door: `GET|PATCH /v1/account/configs/support?
 * target=installation` (master `installationTarget`: the default tenant's layer,
 * for holders of `master.support.approve`, never from an impersonation or a
 * support session; the write guard judges every write). Through the edge the
 * admin door's tenant layer is not reachable, so the remote support page edits
 * the layer here. Same handling as the other account configs: the layers are
 * read with `?layers=1`, a write is a JSON merge patch guarded with `If-Match`,
 * writes of the key go one after another, a version conflict reads again and
 * sends once more, a refusal reads again and says why.
 */
import { ref, shallowRef, type Ref } from 'vue';
import {
  AccountConfigError,
  accountConfigLayerView,
  connectSettingsFieldPatch,
  parseAccountConfigResult,
  withPendingLayerPatches,
  type AccountConfigLayer,
  type AccountConfigLayerView,
  type ConnectSettingsScopeStore,
  type JsonObject,
} from '@blpde/connect';
import { fetchWithAuth, getApiOrigin } from '@blpde/connect/auth';
import { loginId } from '@/auth';
import { SUPPORT_CONFIG_KEY } from './connectSettingsSections';

type Translate = (key: string, named?: Record<string, unknown>) => string;

/** The default tenant's layer, where master keeps the installation's settings. */
const INSTALLATION = { subjectType: 'tenant', subjectId: 1 } as const;

function url(params: Record<string, string>): string {
  const query = new URLSearchParams(params).toString();
  return `${getApiOrigin()}/v1/account/configs/${SUPPORT_CONFIG_KEY}?${query}`;
}

async function refusal(res: Response): Promise<AccountConfigError> {
  const body = (await res.json().catch(() => ({}))) as { code?: unknown; message?: unknown };
  return new AccountConfigError(
    res.status,
    typeof body.code === 'string' ? body.code : '',
    typeof body.message === 'string' ? body.message : ''
  );
}

/**
 * A refusal in words: the write guard's codes (`more_rights`, `not_superadmin`,
 * `invalid_account`, 503 `write_unavailable`), a permission refusal, anything
 * else with master's text.
 */
export function describeInstallationError(t: Translate, err: AccountConfigError | null): string {
  switch (err?.code) {
    case 'more_rights':
      return t('settingsList.errors.moreRights');
    case 'not_superadmin':
      return t('settingsList.errors.notSuperadmin');
    case 'invalid_account':
      return t('settingsList.errors.invalidAccount');
    case 'write_unavailable':
      return t('settingsList.errors.writeUnavailable');
    case 'version_conflict':
      return t('settingsList.errors.conflict');
  }
  if (err?.status === 401 || err?.status === 403) return t('settingsList.errors.notAllowed');
  return err?.message && err.code
    ? t('settingsList.errors.saveDetail', { detail: err.message })
    : t('settingsList.errors.save');
}

/** The store of the settings list's contract for that one layer of that one key. */
export function createInstallationSupportStore(t: Translate): ConnectSettingsScopeStore {
  const layers = shallowRef<AccountConfigLayer[] | null>(null);
  const pending = shallowRef<readonly JsonObject[]>([]);
  const loading = ref(false);
  const error: Ref<string | null> = ref(null);
  let queue: Promise<void> = Promise.resolve();

  /**
   * Bumped by every write (when it starts and when it is answered). A read that
   * started under an older epoch describes a state a write has since changed:
   * its answer is discarded, so a delayed read never overwrites what a write
   * just refreshed.
   */
  let epoch = 0;

  /** Whether a read that started under `startedAt` is still the latest word (no write since). */
  const current = (startedAt: number) => startedAt === epoch;

  async function read(): Promise<boolean> {
    const startedAt = epoch;
    const res = await fetchWithAuth(url({ layers: '1', target: 'installation' }), {
      cache: 'no-store',
    });
    if (!res.ok) throw await refusal(res);
    const parsed = parseAccountConfigResult(await res.json().catch(() => null), SUPPORT_CONFIG_KEY);
    if (!parsed)
      throw new AccountConfigError(res.status, 'invalid_response', 'invalid config response');
    if (!current(startedAt)) return false;
    layers.value = [...(parsed.layers ?? [])];
    return true;
  }

  function baseView(): AccountConfigLayerView | null {
    return layers.value ? accountConfigLayerView(layers.value, INSTALLATION) : null;
  }

  function view(key: string): AccountConfigLayerView | null {
    const base = key === SUPPORT_CONFIG_KEY ? baseView() : null;
    return base ? withPendingLayerPatches(base, pending.value) : null;
  }

  let reading: Promise<void> | null = null;

  /**
   * Reads the layer — every time an editor is entered, so a change another admin
   * made shows; editors entering together share one read.
   */
  async function load(keys: readonly string[]): Promise<void> {
    if (!keys.includes(SUPPORT_CONFIG_KEY)) return;
    reading ??= (async () => {
      loading.value = true;
      const startedAt = epoch;
      try {
        // A read a write has outrun changes neither the state nor the error.
        if (await read()) error.value = null;
      } catch (err) {
        if (current(startedAt)) {
          error.value = describeInstallationError(
            t,
            err instanceof AccountConfigError ? err : null
          );
        }
      } finally {
        loading.value = false;
        reading = null;
      }
    })();
    return reading;
  }

  async function send(patch: JsonObject, retry: boolean, login: string): Promise<void> {
    if (loginId() !== login) return;
    epoch += 1;
    const version = baseView()?.version ?? 0;
    let res: Response;
    try {
      // No backoff: a 503 `write_unavailable` (no edge link) is an answer, not a transient failure.
      res = await fetchWithAuth(
        url({ target: 'installation' }),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'If-Match': String(version) },
          body: JSON.stringify(patch),
        },
        { maxRetries: 0 }
      );
    } finally {
      // Every write ends with an increment, answered or lost: a write whose answer
      // never came may still have committed, so a read begun meanwhile is stale.
      epoch += 1;
    }
    if (res.ok) {
      await read();
      return;
    }
    const failure = await refusal(res);
    if (retry && failure.code === 'version_conflict') {
      await read();
      return send(patch, false, login);
    }
    throw failure;
  }

  function set(key: string, path: readonly string[], value: unknown): void {
    if (key !== SUPPORT_CONFIG_KEY) return;
    const patch = connectSettingsFieldPatch(baseView() ? (view(key)?.own ?? {}) : {}, path, value);
    error.value = null;
    pending.value = [...pending.value, patch];
    // Bound to the login that queued it: after a same-tab session switch the
    // write is dropped, never sent with another account's bearer.
    const login = loginId();
    queue = queue.then(async () => {
      if (loginId() !== login) {
        pending.value = pending.value.filter((item) => item !== patch);
        return;
      }
      try {
        await send(patch, true, login);
      } catch (err) {
        error.value = describeInstallationError(t, err instanceof AccountConfigError ? err : null);
        try {
          await read();
        } catch {
          /* the next read shows it */
        }
      } finally {
        pending.value = pending.value.filter((item) => item !== patch);
      }
    });
  }

  return { load, view, set, loading, error };
}

let shared: ConnectSettingsScopeStore | null = null;

/**
 * The one store of that layer: the remote support page and the rows of the
 * settings list's section „Fernwartung" read and write through it, so they
 * never disagree.
 */
export function installationSupportStore(t: Translate): ConnectSettingsScopeStore {
  shared ??= createInstallationSupportStore(t);
  return shared;
}
